import { Link } from "react-router-dom";
import ArrowRightIcon from "@heroicons/react/24/outline/ArrowRightIcon";

export function HeroSection() {
  const scrollToFeatures = () => {
    const featuresSection = document.getElementById("features");
    if (featuresSection) {
      featuresSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-brand-50 to-white">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-brand-100 rounded-full opacity-50 blur-3xl" />
        <div className="absolute top-60 -left-40 w-80 h-80 bg-brand-100 rounded-full opacity-30 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="text-center lg:text-left">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              Your Personal{" "}
              <span className="text-brand-600">AI Running Coach</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-gray-600 max-w-xl mx-auto lg:mx-0">
              Ask questions about your training. Get real answers. Have actual
              conversations about your running - like talking to a coach who
              knows your data.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 bg-brand-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-brand-700 transition-colors shadow-lg shadow-brand-600/25"
              >
                Get Started Free
                <ArrowRightIcon className="w-4 h-4" />
              </Link>
              <button
                type="button"
                onClick={scrollToFeatures}
                className="inline-flex items-center justify-center gap-2 bg-white text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors border border-gray-200"
              >
                Learn More
              </button>
            </div>
          </div>

          {/* App Screenshot Placeholder */}
          <div className="relative">
            <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
              {/* Mock browser chrome */}
              <div className="bg-gray-100 px-4 py-3 flex items-center gap-2 border-b border-gray-200">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <div className="flex-1 ml-4">
                  <div className="bg-white rounded px-3 py-1 text-xs text-gray-400 max-w-xs">
                    runlens.app
                  </div>
                </div>
              </div>

              {/* Mock chat interface */}
              <div className="p-4 bg-gray-50 space-y-3">
                {/* User message */}
                <div className="flex justify-end">
                  <div className="bg-brand-600 text-white text-sm px-4 py-2 rounded-2xl rounded-br-md max-w-[80%]">
                    Why was my heart rate so high on yesterday's easy run?
                  </div>
                </div>

                {/* AI response */}
                <div className="flex justify-start">
                  <div className="bg-white text-gray-700 text-sm px-4 py-3 rounded-2xl rounded-bl-md shadow-sm max-w-[85%]">
                    <p className="mb-2">Looking at your run from yesterday, your average HR was 156 bpm - about 15 bpm higher than your typical easy runs.</p>
                    <p className="text-gray-500">A few factors could explain this: the 28°C temperature, your shorter recovery after Sunday's long run, or...</p>
                  </div>
                </div>

                {/* Follow-up question */}
                <div className="flex justify-end">
                  <div className="bg-brand-600 text-white text-sm px-4 py-2 rounded-2xl rounded-br-md max-w-[80%]">
                    Should I take an extra rest day?
                  </div>
                </div>

                {/* Input area mock */}
                <div className="bg-white rounded-xl border border-gray-200 p-3 flex items-center gap-2">
                  <div className="flex-1 text-gray-400 text-sm">Ask about your training...</div>
                  <div className="w-8 h-8 bg-brand-600 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating badge */}
            <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900">Real conversations</div>
                  <div className="text-xs text-gray-500">Ask follow-up questions</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
